import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type {
  KnowledgeEntry,
  CreateKnowledgeData,
  UpdateKnowledgeData,
} from '../services/knowledgeService';

const DOMAINS = [
  { value: 'business_process', label: 'Business Process' },
  { value: 'product', label: 'Product' },
  { value: 'customer', label: 'Customer' },
  { value: 'response', label: 'Response Template' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateKnowledgeData | UpdateKnowledgeData) => void;
  isLoading: boolean;
  entry?: KnowledgeEntry | null;
}

export default function KnowledgeFormModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  entry,
}: Props) {
  const isEditing = !!entry;
  const [domain, setDomain] = useState('business_process');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (entry) {
      setDomain(entry.domain);
      setTitle(entry.title);
      setContent(entry.content);
      setTagsInput(entry.tags.join(', '));
    } else {
      setDomain('business_process');
      setTitle('');
      setContent('');
      setTagsInput('');
    }
    setErrors({});
  }, [entry, isOpen]);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors['title'] = 'Title is required';
    if (!content.trim()) newErrors['content'] = 'Content is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    if (isEditing) {
      const data: UpdateKnowledgeData = {};
      if (domain !== entry!.domain) data.domain = domain;
      if (title !== entry!.title) data.title = title;
      if (content !== entry!.content) data.content = content;
      if (JSON.stringify(tags) !== JSON.stringify(entry!.tags)) data.tags = tags;
      onSubmit(data);
    } else {
      onSubmit({ domain, title, content, tags });
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto w-full max-w-2xl rounded-panel bg-surface shadow-l">
          <div className="flex items-center justify-between border-b border-line px-6 py-4">
            <Dialog.Title className="font-display text-lg font-semibold text-ink">
              {isEditing ? 'Edit Entry' : 'New Knowledge Entry'}
            </Dialog.Title>
            <button onClick={onClose} className="text-ink-2 hover:text-ink">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Domain
              </label>
              <select
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
              >
                {DOMAINS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Return Policy"
                className={`w-full rounded-xl border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:ring-1 ${
                  errors['title']
                    ? 'border-danger focus:border-danger focus:ring-danger/20'
                    : 'border-line focus:border-brand focus:ring-brand/20'
                }`}
              />
              {errors['title'] && (
                <p className="mt-1 text-xs text-danger">{errors['title']}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Content
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                placeholder="Write the knowledge base article content here..."
                className={`w-full rounded-xl border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:ring-1 ${
                  errors['content']
                    ? 'border-danger focus:border-danger focus:ring-danger/20'
                    : 'border-line focus:border-brand focus:ring-brand/20'
                }`}
              />
              {errors['content'] && (
                <p className="mt-1 text-xs text-danger">{errors['content']}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Tags
              </label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="shipping, returns, faq (comma-separated)"
                className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
              />
              <p className="mt-1 text-xs text-ink-3">
                Separate tags with commas
              </p>
            </div>

            <div className="flex justify-end gap-3 border-t border-line pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-pill border border-line px-4 py-2 text-sm font-medium text-ink-2 hover:bg-black/[0.04]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-ink disabled:opacity-50"
              >
                {isLoading
                  ? 'Saving...'
                  : isEditing
                    ? 'Save Changes'
                    : 'Create Entry'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
